import React from 'react';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import { formatCurrency } from '../../../../utils/formatters';

const ShippingRateOptions = ({
  sharedAddress,
  loading,
  ratesLoading,
  shippingRates,
  selectedRate,
  handleRateSelection,
}) => {
  if (!sharedAddress) {
    return (
      <div className="p-3 bg-gray-50 border border-gray-200 rounded">
        <p className="text-gray-600 text-sm">
          Please select a shipping address to see shipping options.
        </p>
      </div>
    );
  }

  if (loading || ratesLoading) {
    return <LoadingSpinner />;
  }

  if (shippingRates.length === 0) {
    return (
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
        <p className="text-yellow-800 text-sm">
          No shipping options available for this address. Please try a different address.
        </p>
      </div>
    );
  }

  // Select at most 3 options: cheapest, fastest, and a balanced option
  const limitedRates = (() => {
    if (shippingRates.length <= 3) {
      return shippingRates;
    }

    // Sort by price (ascending)
    const sortedByPrice = [...shippingRates].sort((a, b) => a.price - b.price);
    const cheapest = sortedByPrice[0];
    
    // Sort by delivery time (ascending) if days is available
    const hasDeliveryTime = shippingRates.some(rate => typeof rate.days === 'number' && rate.days > 0);
    let fastest = cheapest;
    if (hasDeliveryTime) {
      const withDays = shippingRates.filter(rate => typeof rate.days === 'number' && rate.days > 0);
      if (withDays.length > 0) {
        fastest = [...withDays].sort((a, b) => a.days - b.days)[0];
      }
    } else {
      // If no delivery times, take the most expensive as "premium"
      fastest = sortedByPrice[sortedByPrice.length - 1];
    }

    // Find a balanced option (not the cheapest or fastest)
    let balanced = cheapest;
    if (sortedByPrice.length > 2) {
      // Take the middle option based on price
      balanced = sortedByPrice[Math.floor(sortedByPrice.length / 2)];
      
      // Ensure we don't duplicate options
      if (balanced.id === cheapest.id || balanced.id === fastest.id) {
        // Find another option that's not cheapest or fastest
        const otherOptions = sortedByPrice.filter(rate => 
          rate.id !== cheapest.id && rate.id !== fastest.id
        );
        if (otherOptions.length > 0) {
          balanced = otherOptions[0];
        }
      }
    }

    // Create a unique set of rates
    const result = [];
    if (cheapest) result.push(cheapest);
    if (fastest && fastest.id !== cheapest.id) result.push(fastest);
    if (balanced && balanced.id !== cheapest.id && balanced.id !== fastest.id) result.push(balanced);
    
    return result;
  })();

  return (
    <div className="space-y-2">
      {limitedRates.map(rate => (
        <div
          key={rate.id}
          className={`p-3 border rounded flex justify-between items-center cursor-pointer transition-colors duration-150 ease-in-out ${selectedRate === rate.id ? 'border-indigo-500 bg-indigo-50 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
          onClick={() => handleRateSelection(rate.id)}
        >
          {/* Rate Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {(typeof rate.days === 'number' && rate.days > 0) 
                ? `Est. Delivery: ${rate.days} day${rate.days > 1 ? 's' : ''}` 
                : rate.service
              }
            </p>
            <p className="text-sm text-gray-500 truncate">
              {(typeof rate.days !== 'number' || rate.days <= 0)
                ? rate.carrier
                : `${rate.service} (${rate.carrier})`
              }
            </p>
          </div>
          <div className={`font-medium text-lg ${selectedRate === rate.id ? 'text-indigo-600' : 'text-gray-700'}`}>
            {formatCurrency(rate.price, rate.currency)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShippingRateOptions; 